import { FC, useEffect, useState } from "react";
import { PaperBillInfoProps } from "../types/PaperBillInfo.types";
import { usePaperBillInfo } from "../hooks/usePaperBill";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import AddIcon from "@mui/icons-material/Add";
import VerifiedIcon from "@mui/icons-material/Verified";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import MenuIcon from "@mui/icons-material/Menu";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import BillSummary from "../../../DirectBilling/NewBill/components/sub-components/BillSummary";
import SpotlightSearch from "../../../DirectBilling/common/components/SpotlightSearch";
import { DeleteConfirmationDialog } from "../../../DirectBilling/common/exports";
import { PaperBillTable } from "./PaperBillTable";
import { usePaperBillSummary } from "../../Edit/hooks/usePaperBillSummary";
import { TransactionsContext } from "../../../../../../stores/Transactions/Transactions.provider";
import useStore from "../../../../../../helpers/useStore";
import { observer } from "mobx-react";
import { BillingStepper } from "../../../../Base/components/Stepper";
import { WaitingBackdrop } from "../../../../Base/components/BackDrop";

const PaperNewBillInfo: FC<PaperBillInfoProps> = observer((props) => {
  const {
    // state
    openDeleteDialog,
    tableVisible,
    isLoading,
    bills,
    notes,
    isSaving,

    // handlers
    handleLoadAll,
    handleAddSingleRx,
    setSelectedRxId,
    handleValidationFilter,
    handleSave,
    handleDeleteCancel,
    handleDeleteConfirm,
    setNotes,
    handleDeleteClick,

    onRowClick,
    selectedRxId,
    anchorEl,
    handleMenuOpen,
    handleMenuClose,
    handleRemoveAll,
  } = usePaperBillInfo(props);

  const { account, userName } = props;

  const {
    totalRxs,
    passedValidationRxs,
    problematicRxs,
    taxiertRxs,
    numberOfPayers,
  } = usePaperBillSummary({ bills });
  const [activeStep, setActiveStep] = useState(0);
  const [headerMenuAnchorEl, setHeaderMenuAnchorEl] =
    useState<null | HTMLElement>(null);
  const headerMenuOpen = Boolean(headerMenuAnchorEl);

  const [isSpotlightOpen, setIsSpotlightOpen] = useState(false);
  const transactionStore = useStore(TransactionsContext);
  const { transactionRefresh } = transactionStore;

  const handleHeaderMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setHeaderMenuAnchorEl(event.currentTarget);
  };

  const handleHeaderMenuClose = () => {
    setHeaderMenuAnchorEl(null);
  };

  const handleHeaderMenuAction = (action: () => void) => {
    action();
    handleHeaderMenuClose();
  };

  const handleSaveClicked = async () => {
    await handleSave();
    setActiveStep(1);
  };

  useEffect(() => {
    handleLoadAll();
    setActiveStep(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionRefresh]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <WaitingBackdrop isWorking={isSaving} message="Bitte warten" />
      <Box
        sx={{
          padding: 1,
          position: "sticky",
          top: 0,
          zIndex: 1000,
          backgroundColor: "#5c758a",
          borderRadius: "0px 0px 0px 0px",
        }}
      >
        <BillingStepper activeStep={activeStep} isPaperBilling={true} />
        {/* Header Buttons */}
        <Grid container justifyContent="space-between">
          <Grid item className="header-button-container" xs={0}>
            <IconButton
              onClick={handleHeaderMenuClick}
              sx={{
                backgroundColor: "white",
                color: "#000000ff",
                padding: 1.25,
                "&:hover": {
                  backgroundColor: "#003059",
                  color: "white",
                },
              }}
            >
              <MenuIcon fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={headerMenuAnchorEl}
              open={headerMenuOpen}
              onClose={handleHeaderMenuClose}
              MenuListProps={{
                "aria-labelledby": "actions-button",
              }}
            >
              <MenuItem onClick={() => handleHeaderMenuAction(handleLoadAll)}>
                <ListItemIcon>
                  <FileDownloadIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Alle laden</ListItemText>
              </MenuItem>

              <MenuItem
                onClick={() =>
                  handleHeaderMenuAction(() => setIsSpotlightOpen(true))
                }
              >
                <ListItemIcon>
                  <AddIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Rezept hinzufügen</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() => handleHeaderMenuAction(handleValidationFilter)}
              >
                <ListItemIcon>
                  <VerifiedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>nur Valide</ListItemText>
              </MenuItem>
            </Menu>
          </Grid>

          {/* Center Typography */}
          <Grid item xs>
            <Typography
              variant="h5"
              gutterBottom
              textAlign={"center"}
              fontWeight={"bold"}
              color="#ffffff"
            >
              Zusammenfassung
            </Typography>
          </Grid>

          <Grid item className="header-button-container" xs={0}>
            <Tooltip title="Rechnungnummer Erstellen" arrow>
              <Button
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                onClick={handleSaveClicked}
                sx={{
                  borderRadius: 2,
                  backgroundColor: "success.main",
                  "&:hover": {
                    backgroundColor: "success.main",
                  },
                }}
              >
                Weiter
              </Button>
            </Tooltip>
          </Grid>
        </Grid>
      </Box>

      {/* Loading */}
      {isLoading && (
        <Box className="loading-container">
          <CircularProgress color="primary" />
          <Chip
            label="Daten werden geladen, bitte warte..."
            className="loading-chip"
          />
        </Box>
      )}

      {/* If table is visible and we have bills */}
      {tableVisible && bills.length > 0 && (
        <>
          <Box
            sx={{
              padding: 1,
              position: "sticky",
              top: 139,
              zIndex: 1000,
              backgroundColor: "#5c758a",
              borderRadius: "0px 0px 10px 10px",
            }}
          >
            <BillSummary
              totalRxs={totalRxs}
              passedValidationRxs={passedValidationRxs}
              problematicRxs={problematicRxs}
              taxiertRxs={taxiertRxs}
              numberOfPayers={numberOfPayers}
            />
          </Box>
          {bills.map((bill, index) => {
            const { payer, totalPrice } = bill;
            // const { payerDto, totalPrice } = data;
            const totalPriceFormatted = totalPrice.toFixed(2).replace(".", ",");
            const todayString = new Date().toDateString();

            return (
              <Box className="bill-card" key={index}>
                <Box className="bill-card-header">
                  <Grid container justifyContent="left" alignItems="center">
                    <Grid item xs={1}>
                      <IconButton
                        onClick={() => handleRemoveAll(index)}
                        style={{ color: "#ff5722" }}
                      >
                        <RemoveCircleOutlineIcon />
                      </IconButton>
                    </Grid>
                    <Grid item xs={11}>
                      <Box className="bill-card-body">
                        <Typography variant="h6" textAlign="center">
                          {payer?.name}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                  <Box sx={{ m: 2 }}>
                    <Grid
                      container
                      spacing={1}
                      alignItems="center"
                      sx={{ mb: 2 }}
                    >
                      <Grid item xs={3}>
                        <Typography variant="subtitle1" align="center">
                          Mandant
                        </Typography>
                        <Box className="box">
                          <Typography variant="subtitle2">{account}</Typography>
                        </Box>
                      </Grid>

                      <Grid item xs={3}>
                        <Typography variant="subtitle1" align="center">
                          Startdatum
                        </Typography>
                        <Box className="box">
                          <Typography variant="subtitle2">
                            {todayString}
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid item xs={3}>
                        <Typography variant="subtitle1" align="center">
                          Erstellt von
                        </Typography>
                        <Box className="box">
                          <Typography variant="subtitle2">
                            {userName}
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid item xs={3}>
                        <Typography variant="subtitle1" align="center">
                          Gesamtpreis
                        </Typography>
                        <Box className="box">
                          <Typography variant="subtitle2">
                            {totalPriceFormatted} €
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>

                    {/* Now we call DirectBillTable to show the Rxs in a table */}
                    <PaperBillTable
                      bill={bill}
                      selectedRxId={selectedRxId}
                      onRowClick={(row) => {
                        onRowClick(row);
                        setSelectedRxId(row.id);
                      }}
                      onMenuOpen={handleMenuOpen}
                    />
                    <Box className="notes-section">
                      <TextField
                        sx={{ mr: 1, mb: 1 }}
                        className="notes-textfield"
                        placeholder="Notizen hinzufügen..."
                        fullWidth
                        value={notes[bill.payer.ik] || ""}
                        onChange={(e) => {
                          const val = e.target.value;
                          setNotes((prev) => ({
                            ...prev,
                            [payer.ik]: val,
                          }));
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </>
      )}

      <SpotlightSearch
        open={isSpotlightOpen}
        onClose={() => setIsSpotlightOpen(false)}
        onRxSelect={(rx) => {
          handleAddSingleRx(rx.rxNumber);
          setIsSpotlightOpen(false);
        }}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={openDeleteDialog}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleDeleteClick}>Entfernen</MenuItem>
      </Menu>
    </Box>
  );
});

export default PaperNewBillInfo;
